import React, { useRef } from 'react'
import { useDrag, useDrop } from 'react-dnd'
import { FormElementInterface } from 'src/shared/types/entities/form-interface'
import { DragAndDropIcon } from 'src/editor/shared/icons/drag-and-drop-icon'

const FORM_ELEMENT = 'form_element'

export interface FormElementDndItemProps {
  id: FormElementInterface['id']
  children: React.ReactNode
  index: number
  onMove: (dragIndex: number, hoverIndex: number) => void
}

interface DragItemProps {
  index: number
  id: FormElementInterface['id']
  type: string
}

export const FormElementDndItem = ({
  id,
  children,
  index,
  onMove,
}: FormElementDndItemProps) => {
  const ref = useRef<HTMLDivElement>(null)
  const isDraggable = useRef<boolean>(false)
  const [, drop] = useDrop<DragItemProps, void, void>({
    accept: FORM_ELEMENT,
    hover(item: DragItemProps) {
      if (ref.current) {
        const dragIndex = item.index
        const hoverIndex = index

        // NOTE: Don't replace items with themselves
        if (dragIndex !== hoverIndex) {
          onMove(dragIndex, hoverIndex)

          /**
           * NOTE: we're mutating the monitor item here!
           * Generally it's better to avoid mutations,
           * but it's good here for the sake of performance
           * to avoid expensive index searches.
           */
          item.index = hoverIndex
        }
      }
    },
  })

  const [{ isDragging }, drag] = useDrag({
    item: { type: FORM_ELEMENT, id, index },
    canDrag: () => isDraggable.current,
    collect: monitor => ({
      isDragging: monitor.isDragging(),
    }),
  })

  drag(drop(ref))

  return (
    <div
      ref={ref}
      style={{ opacity: isDragging ? 0 : 1 }}
      className="flex items-center gap-2 [&>div]:basis-full"
    >
      <DragAndDropIcon
        className="shrink-0 cursor-move"
        onMouseOver={() => {
          isDraggable.current = true
        }}
        onMouseOut={() => {
          isDraggable.current = false
        }}
      />
      {children}
    </div>
  )
}
